import { useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { X } from 'lucide-react';
import { Buton, Etiket, Girdi, toast } from '@emanetx/ui';
import { EmanetXHata } from '@emanetx/sdk';
import { noktaOlusturIstegi, type NoktaOlusturIstegi } from '@emanetx/shared-types';
import { useNoktaOlustur } from './api.js';

interface YeniNoktaDialogOzellikleri {
  acik: boolean;
  onDegisim: (acik: boolean) => void;
}

const VARSAYILAN: NoktaOlusturIstegi = {
  kod: '',
  ad: '',
  saatDilimi: 'Europe/Istanbul',
  paraBirimi: 'TRY',
  durum: 'KURULUM',
  fotografCekimi: false,
  rezervasyonAktif: true,
};

export function YeniNoktaDialog({ acik, onDegisim }: YeniNoktaDialogOzellikleri) {
  const olustur = useNoktaOlustur();
  const form = useForm<NoktaOlusturIstegi>({
    resolver: zodResolver(noktaOlusturIstegi),
    defaultValues: VARSAYILAN,
  });

  useEffect(() => {
    if (!acik) form.reset(VARSAYILAN);
  }, [acik, form]);

  const gonder = form.handleSubmit(async (v) => {
    try {
      const yeni = await olustur.mutateAsync(v);
      toast.success(`${yeni.kod} oluşturuldu`);
      onDegisim(false);
    } catch (err) {
      const mesaj =
        err instanceof EmanetXHata
          ? err.kod === 'nokta_kod_zaten_var'
            ? 'Bu kod ile başka bir nokta zaten var.'
            : err.message
          : (err as Error).message;
      toast.error(mesaj);
    }
  });

  return (
    <Dialog.Root open={acik} onOpenChange={onDegisim}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 data-[state=open]:animate-in data-[state=open]:fade-in-0 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 fixed left-1/2 top-1/2 z-50 w-[94vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Dialog.Title className="text-fume-900 dark:text-fume-50 text-base font-semibold">
                Yeni Nokta
              </Dialog.Title>
              <Dialog.Description className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
                Bir Raspberry Pi ile eşleştirilecek nokta oluşturun. Cihaz bağlandığında seri no
                buradan görünür.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                aria-label="Kapat"
                className="text-fume-400 hover:text-fume-700 dark:hover:text-fume-100"
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={gonder} className="mt-6 space-y-4" noValidate>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Etiket htmlFor="kod">Kod</Etiket>
                <Girdi
                  id="kod"
                  placeholder="PIAZZA-A"
                  className="uppercase"
                  {...form.register('kod', {
                    onChange: (e) => {
                      e.target.value = e.target.value.toUpperCase();
                    },
                  })}
                />
                {form.formState.errors.kod && (
                  <p className="text-xs text-red-600">{form.formState.errors.kod.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Etiket htmlFor="ad">Ad</Etiket>
                <Girdi id="ad" placeholder="Piazza AVM A Noktası" {...form.register('ad')} />
                {form.formState.errors.ad && (
                  <p className="text-xs text-red-600">{form.formState.errors.ad.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Etiket htmlFor="grupAdi">Grup adı (opsiyonel)</Etiket>
              <Girdi id="grupAdi" placeholder="Piazza AVM" {...form.register('grupAdi')} />
            </div>

            <div className="space-y-1.5">
              <Etiket htmlFor="adres">Adres (opsiyonel)</Etiket>
              <Girdi
                id="adres"
                placeholder="Cumhuriyet Mah. AVM Cad. No:1 Şişli/İstanbul"
                {...form.register('adres')}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Etiket htmlFor="paraBirimi">Para birimi</Etiket>
                <select
                  id="paraBirimi"
                  className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-11 w-full rounded-lg border bg-white px-3 text-sm focus:outline-none focus:ring-2"
                  {...form.register('paraBirimi')}
                >
                  <option value="TRY">₺ TRY</option>
                  <option value="EUR">€ EUR</option>
                  <option value="USD">$ USD</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Etiket htmlFor="durum">Durum</Etiket>
                <select
                  id="durum"
                  className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-11 w-full rounded-lg border bg-white px-3 text-sm focus:outline-none focus:ring-2"
                  {...form.register('durum')}
                >
                  <option value="KURULUM">Kurulum</option>
                  <option value="AKTIF">Aktif</option>
                  <option value="BAKIM">Bakım</option>
                  <option value="PASIF">Pasif</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <Buton stil="cizgili" disabled={olustur.isPending}>
                  Vazgeç
                </Buton>
              </Dialog.Close>
              <Buton type="submit" disabled={olustur.isPending}>
                {olustur.isPending ? 'Oluşturuluyor...' : 'Oluştur'}
              </Buton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
